import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  X, Loader2, Phone, Droplets, AlertTriangle, Shield, ExternalLink,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { clsx } from 'clsx';
import { patientApi, api } from '@/lib/api';

interface Props {
  patientId: string | null;
  isOpen:    boolean;
  onClose:   () => void;
}

const INVOICE_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  paid:           { label: 'Paid',         cls: 'bg-green-100 text-green-700' },
  final:          { label: 'Unpaid',        cls: 'bg-blue-100 text-blue-700' },
  partially_paid: { label: 'Partial',       cls: 'bg-teal-100 text-teal-700' },
  draft:          { label: 'Draft Invoice', cls: 'bg-amber-100 text-amber-700' },
  cancelled:      { label: 'Cancelled',     cls: 'bg-gray-100 text-gray-400' },
};

export default function PatientQuickViewModal({ patientId, isOpen, onClose }: Props) {
  const navigate = useNavigate();

  const { data: patient, isLoading } = useQuery({
    queryKey: ['patient-quick', patientId],
    queryFn:  () => patientApi.get(patientId!).then(r => r.data),
    enabled:  !!patientId && isOpen,
    staleTime: 60_000,
  });

  const { data: timelineData } = useQuery({
    queryKey: ['patient-quick-timeline', patientId],
    queryFn:  () => api.get(`/patients/${patientId}/encounters`).then(r => r.data),
    enabled:  !!patientId && isOpen,
    staleTime: 30_000,
  });

  // Pick most recent encounter (not appointment row) for last-visit summary
  const lastEncounter = (timelineData?.data ?? []).find((r: any) => r.record_type === 'encounter');

  if (!isOpen || !patientId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <span className="text-sm font-semibold text-navy">Patient Summary</span>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 transition rounded-lg hover:bg-gray-100">
            <X size={15}/>
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={20} className="animate-spin text-gray-300"/>
          </div>
        ) : !patient ? (
          <div className="py-10 text-center text-sm text-gray-400">Patient not found</div>
        ) : (
          <>
            {/* Demographics */}
            <div className="px-5 pt-4 pb-3">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-navy/10 flex items-center justify-center
                                text-navy font-bold text-lg flex-shrink-0 select-none">
                  {patient.name?.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-navy text-base leading-snug truncate">{patient.name}</div>
                  <code className="text-xs text-gray-400 font-mono">{patient.patient_uid}</code>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {[
                      patient.age_years ? `${patient.age_years}y` : null,
                      patient.gender,
                    ].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>

              <div className="mt-3 space-y-1.5">
                {patient.mobile && (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Phone size={11} className="text-gray-400 flex-none"/>
                    {patient.mobile}
                  </div>
                )}
                {patient.blood_group && (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Droplets size={11} className="text-red-400 flex-none"/>
                    Blood group: <strong className="text-red-600">{patient.blood_group}</strong>
                  </div>
                )}
              </div>

              {/* Clinical flags */}
              {(patient.known_allergies || patient.chronic_conditions) && (
                <div className="mt-3 space-y-1.5">
                  {patient.known_allergies && (
                    <div className="flex items-start gap-2 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                      <AlertTriangle size={11} className="text-red-400 mt-0.5 flex-none"/>
                      <span>
                        <span className="font-semibold text-red-700">Allergy: </span>
                        <span className="text-red-600">{patient.known_allergies}</span>
                      </span>
                    </div>
                  )}
                  {patient.chronic_conditions && (
                    <div className="flex items-start gap-2 text-xs bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">
                      <Shield size={11} className="text-orange-400 mt-0.5 flex-none"/>
                      <span>
                        <span className="font-semibold text-orange-700">Chronic: </span>
                        <span className="text-orange-600">{patient.chronic_conditions}</span>
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Last encounter summary */}
              {lastEncounter && (
                <div className="mt-3 bg-gray-50 rounded-xl px-3 py-2.5">
                  <div className="text-2xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                    Last Visit
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-gray-700">
                      {lastEncounter.event_date
                        ? format(parseISO(lastEncounter.event_date), 'dd MMM yyyy')
                        : '—'}
                      {lastEncounter.doctor_name && (
                        <span className="text-gray-400 ml-1.5">Dr. {lastEncounter.doctor_name}</span>
                      )}
                    </div>
                    {lastEncounter.invoice_status && (() => {
                      const cfg = INVOICE_STATUS_LABEL[lastEncounter.invoice_status];
                      return cfg ? (
                        <span className={clsx('text-2xs font-medium px-2 py-0.5 rounded-full', cfg.cls)}>
                          {cfg.label}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  {lastEncounter.chief_complaint && (
                    <div className="text-2xs text-gray-400 mt-1 truncate" title={lastEncounter.chief_complaint}>
                      {lastEncounter.chief_complaint}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer action */}
            <div className="px-5 pb-4 pt-1">
              <button
                onClick={() => { navigate(`/patients/${patientId}`); onClose(); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white
                           rounded-xl text-sm font-semibold hover:bg-navy/90 transition"
              >
                <ExternalLink size={13}/>
                View Full Profile
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
